import React from 'react';
import { Modal as AntModal } from 'antd';
import { cn } from '@/lib/utils';

const Modal = ({
  open,
  onClose,
  title,
  children,
  width = { xs: '95vw', sm: '90vw', md: '80vw', lg: '70vw', xl: '60vw' },
  maxWidth = { xs: 'full', sm: '2xl', md: '4xl', lg: '5xl', xl: '6xl' },
  minHeight = { xs: '70vh', sm: '75vh', md: '80vh' },
  maxHeight = { xs: '85vh', sm: '80vh', md: '80vh' },
  footer = null,
  centered = true,
  destroyOnClose = true,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  contentClassName = '',
  maskClassName = '',
}) => {
  // Helper function to get responsive value
  const getResponsiveValue = (value, defaultValue = value) => {
    if (typeof value === 'object' && value !== null) {
      return value;
    }
    return defaultValue;
  };

  // Convert responsive values to CSS variables or direct values
  const responsiveWidth = getResponsiveValue(width);
  const responsiveMaxWidth = getResponsiveValue(maxWidth);
  const responsiveMinHeight = getResponsiveValue(minHeight);
  const responsiveMaxHeight = getResponsiveValue(maxHeight);

  // Generate responsive width styles
  const getWidthStyle = () => {
    if (typeof responsiveWidth === 'object') {
      return {
        width: responsiveWidth.xs || '95vw',
        '@media (min-width: 640px)': { width: responsiveWidth.sm || '90vw' },
        '@media (min-width: 768px)': { width: responsiveWidth.md || '80vw' },
        '@media (min-width: 1024px)': { width: responsiveWidth.lg || '70vw' },
        '@media (min-width: 1280px)': { width: responsiveWidth.xl || '60vw' },
      };
    }
    return { width: responsiveWidth };
  };

  // Generate max-width class
  const getMaxWidthClass = () => {
    if (typeof responsiveMaxWidth === 'object') {
      const classes = [];
      if (responsiveMaxWidth.xs) classes.push(`max-w-${responsiveMaxWidth.xs}`);
      if (responsiveMaxWidth.sm) classes.push(`sm:max-w-${responsiveMaxWidth.sm}`);
      if (responsiveMaxWidth.md) classes.push(`md:max-w-${responsiveMaxWidth.md}`);
      if (responsiveMaxWidth.lg) classes.push(`lg:max-w-${responsiveMaxWidth.lg}`);
      if (responsiveMaxWidth.xl) classes.push(`xl:max-w-${responsiveMaxWidth.xl}`);
      return classes.join(' ');
    }
    return `max-w-${responsiveMaxWidth}`;
  };

  // Generate responsive height classes for body
  const getHeightClasses = () => {
    const minHeightClasses = [];
    const maxHeightClasses = [];
    
    if (typeof responsiveMinHeight === 'object') {
      if (responsiveMinHeight.xs) minHeightClasses.push(`min-h-[${responsiveMinHeight.xs}]`);
      if (responsiveMinHeight.sm) minHeightClasses.push(`sm:min-h-[${responsiveMinHeight.sm}]`);
      if (responsiveMinHeight.md) minHeightClasses.push(`md:min-h-[${responsiveMinHeight.md}]`);
      if (responsiveMinHeight.lg) minHeightClasses.push(`lg:min-h-[${responsiveMinHeight.lg}]`);
      if (responsiveMinHeight.xl) minHeightClasses.push(`xl:min-h-[${responsiveMinHeight.xl}]`);
    } else {
      minHeightClasses.push(`min-h-[${responsiveMinHeight}]`);
    }

    if (typeof responsiveMaxHeight === 'object') {
      if (responsiveMaxHeight.xs) maxHeightClasses.push(`max-h-[${responsiveMaxHeight.xs}]`);
      if (responsiveMaxHeight.sm) maxHeightClasses.push(`sm:max-h-[${responsiveMaxHeight.sm}]`);
      if (responsiveMaxHeight.md) maxHeightClasses.push(`md:max-h-[${responsiveMaxHeight.md}]`);
      if (responsiveMaxHeight.lg) maxHeightClasses.push(`lg:max-h-[${responsiveMaxHeight.lg}]`);
      if (responsiveMaxHeight.xl) maxHeightClasses.push(`xl:max-h-[${responsiveMaxHeight.xl}]`);
    } else {
      maxHeightClasses.push(`max-h-[${responsiveMaxHeight}]`);
    }

    return [...minHeightClasses, ...maxHeightClasses].join(' ');
  };

  return (
    <AntModal
      title={title}
      open={open}
      onCancel={onClose}
      width={typeof responsiveWidth === 'object' ? responsiveWidth.xs || '95vw' : responsiveWidth}
      className={cn(
        'custom-modal',
        getMaxWidthClass(),
        'w-full mx-auto', // Ensure proper centering
        className
      )}
      classNames={{
        header: cn(
          '!mb-0 !border-b',
          '!p-1 sm:!p-2 md:!p-3', // Responsive padding
          '!text-sm sm:!text-base md:!text-lg', // Responsive text size
          headerClassName
        ),
        body: cn(
          '!overflow-auto',
          '!p-1 sm:!p-2 md:!p-3', // Responsive padding
          getHeightClasses(),
          bodyClassName
        ),
        content: cn(
          '!rounded-lg sm:!rounded-xl', // Responsive border radius
          '!mx-2 sm:!mx-4 md:!mx-auto', // Responsive margins
          contentClassName
        ),
        mask: cn('!bg-black/30', maskClassName),
        wrapper: '!flex !items-center !justify-center !p-2 sm:!p-4',
        root: '!flex !items-center !justify-center'
      }}
      centered={centered}
      footer={footer}
      destroyOnHidden={destroyOnClose}
      style={{
        ...getWidthStyle(),
        maxWidth: typeof responsiveMaxWidth === 'object' 
          ? undefined 
          : responsiveMaxWidth === 'full' ? '100vw' : `var(--max-w-${responsiveMaxWidth})`
      }}
      // Add responsive behavior for different screen sizes
      getContainer={() => {
        // Ensure modal container adapts to screen size
        return document.body;
      }}
    >
      <div className="w-full h-full">
        {children}
      </div>
    </AntModal>
  );
};

export { Modal };